import {
    defineComponent,
    onMounted,
    PropType,
    ref,
} from "vue";
import { useLocale } from "../../locales/i18n.js";
import type { LogEntry } from "../../core/types.js";
import {
    diffPermissionBits,
} from "./role/permissions.js";
import type { PermissionDiffEntry } from "./role/permissions.js";
import {
    getRolePrimaryColorCss,
    normalizeRoleColors,
    roleColorGradientToCss,
    roleColorToCss,
} from "../../shared/roles/roleColors.js";
import type { RoleColors } from "../../shared/roles/roleColors.js";

type RoleChange = {
    key?: string;
    old?: unknown;
    new?: unknown;
};

type RoleSnapshot = {
    id: string;
    name?: string;
    color?: number;
    colors?: RoleColors | null;
    icon?: string | null;
    iconURL?: string | null;
    unicodeEmoji?: string | null;
    unciodeEmoji?: string | null;
    position?: number;
};

type RoleStyle = Record<"--role-color" | "--role-dot-background", string>;

const ChangeLabelKeys = new Map<string, string>([
    [ "name", "role_preview.change_fields.name" ],
    [ "color", "role_preview.change_fields.color" ],
    [ "colors", "role_preview.change_fields.colors" ],
    [ "hoist", "role_preview.change_fields.hoist" ],
    [ "mentionable", "role_preview.change_fields.mentionable" ],
    [ "permissions", "role_preview.change_fields.permissions" ],
    [ "icon", "role_preview.change_fields.icon" ],
    [ "unicode_emoji", "role_preview.change_fields.emoji" ],
    [ "unicodeEmoji", "role_preview.change_fields.emoji" ],
    [ "position", "role_preview.change_fields.position" ],
    [ "managed", "role_preview.change_fields.managed" ],
]);

let rolesCache: Promise<Map<string, RoleSnapshot>> | null = null;

function getRoleStyle(role: RoleSnapshot): RoleStyle {
    return {
        "--role-color"          : getRolePrimaryColorCss(role),
        "--role-dot-background" : roleColorGradientToCss(role),
    };
}

function isRoleChange(value: unknown): value is RoleChange {
    return typeof value === "object" && value !== null && "key" in value;
}

function getChanges(log: LogEntry): RoleChange[] {
    const changes = log.extra?.changes;

    if (!Array.isArray(changes))
        return [];

    return changes.filter(isRoleChange);
}

function getChangeValue(changes: readonly RoleChange[], key: string, side: "old" | "new"): unknown {
    return changes.find((change) => change.key === key)?.[side];
}

function getRoleId(log: LogEntry): string {
    const role = log.extra?.role;

    if (typeof role === "string")
        return role;

    if (typeof role === "object" && role !== null && "id" in role && typeof role.id === "string")
        return role.id;

    return String(log.role ?? "");
}

function getRoleFromPositionLog(log: LogEntry): RoleSnapshot | null {
    const role = log.extra?.role;

    if (typeof role !== "object" || role === null)
        return null;

    if (!("id" in role) || typeof role.id !== "string")
        return null;

    return role as RoleSnapshot;
}

function buildRoleFromChanges(log: LogEntry): RoleSnapshot {
    const changes = getChanges(log);
    const id = getRoleId(log);
    const side = log.type === "role_delete" ? "old" : "new";
    const fallbackSide = side === "new" ? "old" : "new";

    function pick(key: string): unknown {
        return getChangeValue(changes, key, side) ?? getChangeValue(changes, key, fallbackSide);
    }

    return {
        id,
        name         : typeof pick("name") === "string" ? String(pick("name")) : undefined,
        color        : typeof pick("color") === "number" ? Number(pick("color")) : undefined,
        colors       : typeof pick("colors") === "object" && pick("colors") !== null ? pick("colors") as RoleColors : undefined,
        icon         : typeof pick("icon") === "string" ? String(pick("icon")) : undefined,
        unicodeEmoji : typeof pick("unicode_emoji") === "string" ? String(pick("unicode_emoji")) : undefined,
        position     : typeof pick("position") === "number" ? Number(pick("position")) : undefined,
    };
}

function mergeRoleSnapshots(...roles: Array<RoleSnapshot | null | undefined>): RoleSnapshot {
    const result: Record<string, unknown> = {};

    for (const role of roles) {
        if (!role)
            continue;

        for (const [ key, value ] of Object.entries(role)) {
            if (value !== undefined && value !== null && value !== "")
                result[key] = value;
        }
    }

    return result as RoleSnapshot;
}

function getRoleIconUrl(role: RoleSnapshot): string | null {
    const rawIcon = role.iconURL ?? role.icon;

    if (!rawIcon)
        return null;

    if (rawIcon.startsWith("http://") || rawIcon.startsWith("https://"))
        return rawIcon;

    return `https://cdn.discordapp.com/role-icons/${role.id}/${rawIcon}.webp?size=64`;
}

function getRoleEmoji(role: RoleSnapshot): string | null {
    return role.unicodeEmoji ?? role.unciodeEmoji ?? null;
}

function getChangeLabel(key: string | undefined, i18n: (key: string) => string): string {
    if (!key)
        return i18n("role_preview.change_fields.unknown");

    const labelKey = ChangeLabelKeys.get(key);

    return labelKey ? i18n(labelKey) : key;
}

function stringifyObject(value: object): string {
    try {
        return JSON.stringify(value);
    } catch {
        return "[objet]";
    }
}

function formatPrimitiveValue(value: unknown): string {
    if (value === null || value === undefined || value === "")
        return "—";

    if (typeof value === "boolean")
        return value ? "Oui" : "Non";

    if (typeof value === "number" || typeof value === "bigint")
        return String(value);

    if (typeof value === "string")
        return value;

    if (Array.isArray(value))
        return value.length ? value.map(formatPrimitiveValue).join(", ") : "—";

    if (typeof value === "object")
        return stringifyObject(value);

    return String(value);
}

function formatColorValue(value: unknown) {
    const label = formatPrimitiveValue(value);

    if (typeof value !== "number" || value <= 0)
        return <span>{label}</span>;

    return (
        <span class="role-preview-color-value">
            <span class="role-preview-color-swatch" style={{ "--role-color": roleColorToCss(value) } as Record<string, string>} />
            {roleColorToCss(value)}
        </span>
    );
}

function formatRoleColorsValue(value: unknown, i18n: (key: string) => string) {
    if (typeof value !== "object" || value === null)
        return <span>{formatPrimitiveValue(value)}</span>;

    const colors = normalizeRoleColors({ colors: value as RoleColors });
    const cssGradient = roleColorGradientToCss({ colors });
    const labels = [
        colors.primaryColor,
        colors.secondaryColor,
        colors.tertiaryColor,
    ].map((color) => roleColorToCss(color));

    return (
        <div class="role-preview-colors-value">
            <span class="role-preview-colors-swatch" style={{ "--role-dot-background": cssGradient } as Record<string, string>} />
            <div>
                <span>{i18n("role_preview.colors.primary")} {labels[0]}</span>
                {colors.secondaryColor && <span>{i18n("role_preview.colors.secondary")} {labels[1]}</span>}
                {colors.tertiaryColor && <span>{i18n("role_preview.colors.tertiary")} {labels[2]}</span>}
            </div>
        </div>
    );
}

function formatChangeValue(key: string | undefined, value: unknown, i18n: (key: string) => string) {
    if (key === "color")
        return formatColorValue(value);

    if (key === "colors")
        return formatRoleColorsValue(value, i18n);

    return <span>{formatPrimitiveValue(value)}</span>;
}

function getPermissionChanges(changes: readonly RoleChange[]): PermissionDiffEntry[] {
    const permissionChange = changes.find((change) => change.key === "permissions");

    if (!permissionChange)
        return [];

    return diffPermissionBits(permissionChange.old, permissionChange.new);
}

function loadRoles(): Promise<Map<string, RoleSnapshot>> {
    rolesCache ??= fetch("/api/roles")
        .then((response) => response.ok ? response.json() : [])
        .then((roles: unknown) => {
            if (!Array.isArray(roles))
                return new Map<string, RoleSnapshot>();

            return new Map(roles
                .filter((role): role is RoleSnapshot => {
                    return typeof role === "object"
                        && role !== null
                        && "id" in role
                        && typeof role.id === "string";
                })
                .map((role) => [ role.id, role ]));
        })
        .catch(() => new Map<string, RoleSnapshot>());

    return rolesCache;
}

export default defineComponent({
    props: {
        log: {
            type: Object as PropType<LogEntry>,
            required: true,
        },
    },

    setup(props) {
        const { i18n } = useLocale();
        const roles = ref(new Map<string, RoleSnapshot>());

        onMounted(async () => {
            roles.value = await loadRoles();
        });

        function resolveRole(): RoleSnapshot {
            const id = getRoleId(props.log);
            const cachedRole = id ? roles.value.get(id) : null;
            const loggedRole = getRoleFromPositionLog(props.log);
            const changedRole = buildRoleFromChanges(props.log);

            return mergeRoleSnapshots({
                id,
                name: id ? `Rôle ${id}` : "Rôle inconnu",
            }, cachedRole, changedRole, loggedRole);
        }

        function renderRoleIcon(role: RoleSnapshot) {
            const iconUrl = getRoleIconUrl(role);
            const emoji = getRoleEmoji(role);

            if (iconUrl)
                return <img class="role-preview-role-icon" src={iconUrl} alt="" />;

            if (emoji)
                return <span class="role-preview-role-emoji">{emoji}</span>;

            return null;
        }

        function renderSummary(role: RoleSnapshot) {
            return (
                <div class="role-preview-summary">
                    <div class="role-preview-role-chip" style={getRoleStyle(role)}>
                        <span class="role-preview-role-dot" />
                        {renderRoleIcon(role)}
                        <span class="role-preview-role-name">{role.name || role.id}</span>
                    </div>

                    <div class="role-preview-role-meta">
                        <span>ID {role.id || "inconnu"}</span>
                        {typeof role.position === "number" && <span>Position {role.position}</span>}
                    </div>
                </div>
            );
        }

        function renderPositionChange() {
            if (props.log.type !== "role_position_update")
                return null;

            return (
                <div class="role-preview-card">
                    <h3>Position du rôle</h3>
                    <div class="role-preview-position-change">
                        <span>{formatPrimitiveValue(props.log.extra?.oldPosition)}</span>
                        <span class="material-symbols-outlined">arrow_forward</span>
                        <span>{formatPrimitiveValue(props.log.extra?.newPosition)}</span>
                    </div>
                </div>
            );
        }

        function renderPermissionChange(permission: PermissionDiffEntry) {
            const isAdded = permission.state === "added";

            return (
                <div class={`role-preview-permission role-preview-permission-${permission.state}`}>
                    <span class="material-symbols-outlined">
                        {isAdded ? "add_circle" : "remove_circle"}
                    </span>
                    <div>
                        <strong>{i18n(`permissions.${permission.key}.name`)}</strong>
                        <span>{i18n(`permissions.${permission.key}.description`)}</span>
                    </div>
                    {permission.privileged && (
                        <span class="role-preview-permission-badge">
                            {i18n("role_preview.permissions.privileged")}
                        </span>
                    )}
                </div>
            );
        }

        function renderPermissionChanges() {
            const permissionChanges = getPermissionChanges(getChanges(props.log));

            if (!permissionChanges.length)
                return null;

            const addedCount = permissionChanges.filter((permission) => permission.state === "added").length;
            const removedCount = permissionChanges.length - addedCount;

            return (
                <details class="role-preview-card role-preview-permissions">
                    <summary>
                        <span>
                            {i18n("role_preview.permissions.title")}
                        </span>
                        <span>
                            {i18n("role_preview.permissions.summary", addedCount, removedCount)}
                        </span>
                        <span class="material-symbols-outlined">expand_more</span>
                    </summary>
                    <div class="role-preview-permission-list">
                        {permissionChanges.map((permission) => renderPermissionChange(permission))}
                    </div>
                </details>
            );
        }

        function renderChanges() {
            const rawChanges = getChanges(props.log);
            const hasPermissionDetails = getPermissionChanges(rawChanges).length > 0;
            const changes = rawChanges
                .filter((change) => change.key !== "permissions" || !getPermissionChanges([ change ]).length);

            if (props.log.type === "role_position_update")
                return null;

            if (!changes.length && hasPermissionDetails)
                return null;

            if (!changes.length) {
                return (
                    <div class="role-preview-card">
                        <h3>{i18n("role_preview.changes.title")}</h3>
                        <p class="role-preview-empty">{i18n("role_preview.changes.empty")}</p>
                    </div>
                );
            }

            return (
                <div class="role-preview-card">
                    <h3>{i18n("role_preview.changes.title")}</h3>
                    <div class="role-preview-change-list">
                        {changes.map((change, index) => (
                            <div class="role-preview-change" key={`${change.key ?? "unknown"}-${index}`}>
                                <div class="role-preview-change-field">{getChangeLabel(change.key, i18n)}</div>
                                <div class="role-preview-change-values">
                                    <div>
                                        <span>{i18n("role_preview.changes.before")}</span>
                                        {formatChangeValue(change.key, change.old, i18n)}
                                    </div>
                                    <span class="material-symbols-outlined">arrow_forward</span>
                                    <div>
                                        <span>{i18n("role_preview.changes.after")}</span>
                                        {formatChangeValue(change.key, change.new, i18n)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        function renderReason() {
            const reason = props.log.extra?.reason;

            if (typeof reason !== "string" || !reason.trim())
                return null;

            return (
                <div class="role-preview-card">
                    <h3>{i18n("role_preview.reason.title")}</h3>
                    <p>{reason}</p>
                </div>
            );
        }

        return () => {
            const role = resolveRole();

            return (
                <div class="role-preview">
                    {renderSummary(role)}
                    {renderPositionChange()}
                    {renderPermissionChanges()}
                    {renderChanges()}
                    {renderReason()}
                </div>
            );
        };
    },
});
